import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from 'react';
import {
  doc,
  getFirestore,
  getDocs,
  onSnapshot,
  collection,
  setDoc,
  addDoc,
  deleteDoc,
  updateDoc,
  where,
  query,
  and,
} from 'firebase/firestore';
import {arrayMove} from '@dnd-kit/sortable';
import {alertError} from 'components/completion/Alerts';
import {
  roleToFirebaseString,
  isExperiencedStudent,
  isMinimumRole,
} from 'contexts/profileUtil';
import {ProfileContext} from 'contexts/ProfileContext';
import {Role} from 'types/role';
import {useServerTimeOnce} from 'contexts/TimeContext';
import {toRoadmapClockString} from 'course/editors/roadmapEditor/releaseStatus';
import {RoleFirebaseString} from 'types/role';
import {useTranslateFunction} from 'contexts/translation/TranslationContext';
import type {
  RoadmapAssignmentData,
  RoadmapItem,
  RoadmapLessonData,
  RoadmapModule,
  RoadmapSelfCheckData,
} from 'course/editors/roadmapEditor/roadmapSchema';
import type {Active, Over} from '@dnd-kit/core';
import {selectUpcomingShownModules} from './nextReleaseModules';
import {selectRoadmapAssignments} from './roadmapAssignments';

/** The fields an admin supplies when creating a module. */
export type RoadmapModuleDraft = Pick<RoadmapModule, 'title' | 'startDate'>;

interface RoadmapContextType {
  roadmapItemMap: {[key: string]: RoadmapModule};
  /**
   * For a student, the unreleased modules they see on the roadmap, locked:
   * the next week, and any a module's `roadmapVisibility` shows or hides
   * (see {@link selectUpcomingShownModules}). Map views show them locked;
   * nothing else reads them. Empty for other roles, which load every module
   * they may see into `roadmapItemMap`.
   */
  nextRoadmapItemMap: {[key: string]: RoadmapModule};
  roadmapLoading: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  getCurrentTeachingModule: () => any;
  deleteModuleFromDB: (moduleId: string) => void;
  /**
   * Creates a module in the given roadmap and returns its new id, or
   * `undefined` when the write failed (the failure is already surfaced to
   * the user).
   */
  createModuleInDB: (
    modInfo: RoadmapModuleDraft,
    options: {roadmapType: RoleFirebaseString},
  ) => Promise<string | undefined>;
  deleteItemFromDB: (itemId: string, moduleId: string) => void;
  createItemInDB: (item: RoadmapItem, moduleId: string) => void;
  /** Merges `patch` into the module document; omitted fields are untouched. */
  editModuleInDB: (
    moduleId: string,
    patch: Partial<RoadmapModule>,
  ) => Promise<void>;
  editItemInDB: (moduleId: string, newItem: RoadmapItem) => void;
  bulkEditItemsInDB: (
    moduleId: string,
    updatedItems: RoadmapItem[],
  ) => Promise<void>;
  swapItemOrderInDB: (active: Active, over: Over, moduleId: string) => void;
  getCommentPath: (moduleId: string) => string;
  addModuleImageLink: (url: string, moduleId: string) => void;
  /**
   * The student roadmap's published assignments, oldest week first.
   * `onlyReleased` keeps modules released by now (server time); omitted →
   * every module. `includeMathAssignments` adds Math IDE assignments;
   * omitted → only Python IDE ones.
   */
  getAssignmentsInRoadmap: (
    onlyReleased?: boolean,
    options?: {includeMathAssignments?: boolean},
  ) => RoadmapAssignmentData[];
  getLessonsInRoadmap: (onlyReleased?: boolean) => RoadmapLessonData[];
  getTeacherLessonsInRoadmap: (onlyReleased?: boolean) => RoadmapLessonData[];
  getSelfChecksInRoadmap: (
    onlyReleased?: boolean,
    forRole?: RoleFirebaseString,
  ) => RoadmapSelfCheckData[];
}

// Create the context with initial undefined value
export const RoadmapContext = createContext<RoadmapContextType | undefined>(
  undefined,
);

interface RoadmapProviderProps {
  children: ReactNode;
  courseId: string;
}

export const RoadmapProvider: React.FC<RoadmapProviderProps> = ({
  courseId,
  children,
}) => {
  const t = useTranslateFunction('miscUI');

  const [roadmapItemMap, setRoadmapItemMap] = useState<
    Record<string, RoadmapModule>
  >({});
  const [nextRoadmapItemMap, setNextRoadmapItemMap] = useState<
    Record<string, RoadmapModule>
  >({});
  const [roadmapLoading, setRoadmapLoading] = useState(true);
  const {userData} = useContext(ProfileContext);
  const db = getFirestore();
  const serverTimeMs = useServerTimeOnce();
  const [todayStr, setTodayStr] = useState('');

  const courseRole = userData?.courseRole;

  const roadmapModulesCollectionRef = (courseRole: Role) => {
    switch (courseRole) {
      case Role.INSTRUCTOR:
      case Role.ADMIN:
        return collection(db, `roadmap/${courseId}/modules`);
      case Role.SECTION_LEADER:
        return query(
          collection(db, `roadmap/${courseId}/modules`),
          where('startDate', '<=', todayStr),
        );
      case Role.STUDENT:
        return query(
          collection(db, `roadmap/${courseId}/modules`),
          and(
            where('roadmapType', '==', roleToFirebaseString(Role.STUDENT)),
            where('startDate', '<=', todayStr),
          ),
        );
      case Role.EXPERIENCED_STUDENT:
        return query(
          collection(db, `roadmap/${courseId}/modules`),
          and(
            where(
              'roadmapType',
              '==',
              roleToFirebaseString(Role.EXPERIENCED_STUDENT),
            ),
            where('startDate', '<=', todayStr),
          ),
        );
      default:
        return query(
          collection(db, `roadmap/${courseId}/modules`),
          where('startDate', '<=', todayStr),
        );
    }
  };

  const getCurrentTeachingModule = async () => {
    // fetch modules which are active and not student modules
    const currentModuleQuery = query(
      collection(db, `roadmap/${courseId}/modules`),
      where('startDate', '<=', todayStr),
      where('roadmapType', '==', 'sl'),
    );
    const modules = await getDocs(currentModuleQuery);
    if (modules.empty) {
      return null;
    }
    const sortedModules = modules.docs.sort((a, b) =>
      a.data().startDate > b.data().startDate ? 1 : -1,
    );
    // NOTE only returns one module -- the one with the most recent start date
    return sortedModules.pop();
  };

  useEffect(() => {
    if (courseRole === undefined) return;
    if (!todayStr) return;

    // A live subscription rather than a one-shot read, so edits made in the
    // roadmap editor (in this tab or another) reach every open home page.
    const unsubscribe = onSnapshot(
      roadmapModulesCollectionRef(courseRole),
      snapshot => {
        const roadmap = {};
        for (const moduleDoc of snapshot.docs) {
          roadmap[moduleDoc.id] = moduleDoc.data();
        }
        setRoadmapItemMap(roadmap);
        setRoadmapLoading(false);
      },
      error => {
        console.error('[RoadmapContext] modules subscription failed', error);
        setRoadmapLoading(false);
      },
    );
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, [courseRole, todayStr]);

  useEffect(() => {
    const isStudentRoadmap =
      courseRole === Role.STUDENT || courseRole === Role.EXPERIENCED_STUDENT;
    if (!isStudentRoadmap || !todayStr) return;
    const unsubscribe = onSnapshot(
      query(
        collection(db, `roadmap/${courseId}/modules`),
        and(
          where('roadmapType', '==', roleToFirebaseString(courseRole)),
          where('startDate', '>', todayStr),
        ),
      ),
      snapshot => {
        const upcoming = {};
        for (const moduleDoc of snapshot.docs) {
          upcoming[moduleDoc.id] = moduleDoc.data();
        }
        setNextRoadmapItemMap(selectUpcomingShownModules(upcoming));
      },
      error => {
        console.error(
          '[RoadmapContext] next modules subscription failed',
          error,
        );
      },
    );
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- db is a stable singleton
  }, [courseId, courseRole, todayStr]);

  useEffect(() => {
    // Release dates are PT wall-clock strings compared as strings, so "now"
    // must be built in the same fixed format; a locale-formatted hour
    // ("10 AM", "01 PM", "09时") sorts wrong against "10:00".
    if (!serverTimeMs) return;
    // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    setTodayStr(toRoadmapClockString({nowMs: serverTimeMs}));
  }, [serverTimeMs]);

  // Every mutator below writes to Firestore only. The modules subscription
  // (with the SDK's latency compensation) is the single writer of
  // `roadmapItemMap`, so the UI updates instantly without a local mirror.
  const modulePath = (moduleId: string) =>
    `roadmap/${courseId}/modules/${moduleId}`;

  const createModuleInDB = async (
    modInfo: RoadmapModuleDraft,
    options: {roadmapType: RoleFirebaseString},
  ): Promise<string | undefined> => {
    try {
      const docResp = await addDoc(
        collection(db, `roadmap/${courseId}/modules`),
        {...modInfo, roadmapType: options.roadmapType, items: []},
      );
      return docResp.id;
    } catch (e) {
      alertError(e, t);
      return undefined;
    }
  };

  const deleteModuleFromDB = async (moduleId: string) => {
    try {
      await deleteDoc(doc(db, modulePath(moduleId)));
      await deleteDoc(doc(db, `roadmap/${courseId}/descriptions/${moduleId}`));
    } catch (e) {
      alertError(e, t);
    }
  };

  const writeItems = async (moduleId: string, items: RoadmapItem[]) => {
    try {
      await updateDoc(doc(db, modulePath(moduleId)), {items});
    } catch (e) {
      alertError(e, t);
    }
  };

  const createItemInDB = async (item: RoadmapItem, moduleId: string) => {
    if (!item.id) {
      return;
    }
    await writeItems(moduleId, [...roadmapItemMap[moduleId].items, item]);
  };

  const deleteItemFromDB = async (itemId: string, moduleId: string) => {
    await writeItems(
      moduleId,
      roadmapItemMap[moduleId].items.filter(item => item.id !== itemId),
    );
  };

  const editModuleInDB = async (
    moduleId: string,
    patch: Partial<RoadmapModule>,
  ) => {
    try {
      await updateDoc(doc(db, modulePath(moduleId)), patch);
    } catch (e) {
      alertError(e, t);
    }
  };

  const editItemInDB = async (moduleId: string, newItem: RoadmapItem) => {
    if (!newItem.id) {
      return;
    }
    const items = roadmapItemMap[moduleId].items;
    if (!items.some(item => item.id === newItem.id)) {
      return;
    }
    await writeItems(
      moduleId,
      items.map(item => (item.id === newItem.id ? newItem : item)),
    );
  };

  /** Replaces all items in a module with the provided updated items array. */
  const bulkEditItemsInDB = async (
    moduleId: string,
    updatedItems: RoadmapItem[],
  ) => {
    await writeItems(moduleId, updatedItems);
  };

  const swapItemOrderInDB = async (
    active: Active,
    over: Over,
    moduleId: string,
  ) => {
    const items = roadmapItemMap[moduleId].items;
    const oldIdx = items.findIndex(obj => obj.id === active.id);
    const newIdx = items.findIndex(obj => obj.id === over.id);
    await writeItems(moduleId, arrayMove(items, oldIdx, newIdx));
  };

  const addModuleImageLink = async (url: string, moduleId: string) => {
    await setDoc(
      doc(db, modulePath(moduleId)),
      {thumbnailUrl: url},
      {merge: true},
    );
  };

  const getCommentPath = (moduleId: string) => {
    return `roadmap/${courseId}/modules/${moduleId}/announcement`;
  };

  const getAssignmentsInRoadmap = (
    onlyReleased = false,
    {includeMathAssignments = false}: {includeMathAssignments?: boolean} = {},
  ): RoadmapAssignmentData[] => {
    const roleType = isExperiencedStudent(courseRole)
      ? Role.EXPERIENCED_STUDENT
      : Role.STUDENT;
    return selectRoadmapAssignments({
      modules: roadmapItemMap,
      roadmapType: roleToFirebaseString(roleType),
      releasedBy: onlyReleased ? todayStr : undefined,
      includeMathAssignments,
    });
  };

  const getLessonsInRoadmap = (onlyReleased = false): RoadmapLessonData[] => {
    const isES = isExperiencedStudent(courseRole);
    // eslint-disable-next-line prefer-const -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    let roleType = isES ? Role.EXPERIENCED_STUDENT : Role.STUDENT;
    const modules = Object.keys(roadmapItemMap)
      .map(moduleId => {
        return {...roadmapItemMap[moduleId], id: moduleId};
      })
      .filter(
        module =>
          module.roadmapType == roleToFirebaseString(roleType) &&
          (!onlyReleased || module.startDate <= todayStr),
      )
      .sort((a, b) => {
        return a.startDate > b.startDate ? 1 : -1;
      });
    // eslint-disable-next-line prefer-const -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    let lessons = [];
    for (const module of modules) {
      const les = module.items.filter(
        item => item.itemType === 'Lesson' && item.published === true,
      );
      for (const item of les) {
        lessons.push({
          module: {
            title: module.title,
            id: module.id,
          },
          lessonId: item.urlId,
          title: item.title,
          lessonNum: item.lessonNum,
        });
      }
    }
    return lessons;
  };

  const getTeacherLessonsInRoadmap = (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
    onlyReleased = false,
  ): RoadmapLessonData[] => {
    const roadmapModules = Object.keys(roadmapItemMap)
      .filter(moduleId => {
        const isPublished =
          moduleId in roadmapItemMap &&
          roadmapItemMap[moduleId]?.startDate <= todayStr;
        const isSLModule =
          moduleId in roadmapItemMap &&
          roadmapItemMap[moduleId]?.roadmapType == 'sl';

        return isPublished && isSLModule;
      })
      .sort((a, b) => {
        if (!(a in roadmapItemMap) || !(b in roadmapItemMap)) return 0;
        const aModule = roadmapItemMap[a];
        const bModule = roadmapItemMap[b];
        return aModule.startDate > bModule.startDate ? 1 : -1;
      });

    const lessons = [];
    for (let rmModIdx = 0; rmModIdx < roadmapModules.length; rmModIdx++) {
      const module = roadmapItemMap[roadmapModules[rmModIdx]];
      if (!module || !module.items) continue;
      const items = module.items;
      for (let itemIdx = 0; itemIdx < items.length; itemIdx++) {
        const item = items[itemIdx];
        if (item.itemType == 'Lesson') {
          lessons.push({
            module: {
              title: module.title,
              id: module.id,
            },
            lessonId: item.urlId,
            title: item.title,
          });
        }
      }
    }

    return lessons;
  };

  /**
   * Returns self-check items from the roadmap, optionally filtered to only
   * those whose parent module has been released (startDate <= today).
   * Deduplicates by completionId/urlId.
   */
  const getSelfChecksInRoadmap = (
    onlyReleased = false,
    forRole?: RoleFirebaseString,
  ): RoadmapSelfCheckData[] => {
    const effectiveRole =
      forRole ??
      ((): RoleFirebaseString | undefined => {
        const isES = isExperiencedStudent(courseRole);
        const roleType = isES ? Role.EXPERIENCED_STUDENT : Role.STUDENT;
        return roleToFirebaseString(roleType);
      })();

    /**
     * TAs+ should see all roadmap items (i.e. from student roles and the SL role) to allow for easier testing.
     */
    const showAll =
      !forRole && courseRole && isMinimumRole(courseRole, Role.TA);
    const modules = Object.keys(roadmapItemMap)
      .map(moduleId => {
        return {...roadmapItemMap[moduleId], id: moduleId};
      })
      .filter(module => {
        if (showAll) return !onlyReleased || module.startDate <= todayStr;
        return (
          module.roadmapType === effectiveRole &&
          (!onlyReleased || module.startDate <= todayStr)
        );
      })
      .sort((a, b) => {
        return a.startDate > b.startDate ? 1 : -1;
      });
    const selfChecks: RoadmapSelfCheckData[] = [];
    const seenIds = new Set<string>();
    for (const module of modules) {
      const checks = module.items.filter(
        item =>
          item.itemType === 'Self Check' ||
          (item.itemType === 'Link' && item.completionId),
      );
      for (const item of checks) {
        const id = item.completionId || item.urlId;
        if (seenIds.has(id)) continue;
        seenIds.add(id);
        selfChecks.push({
          module: {
            title: module.title,
            id: module.id,
          },
          selfCheckId: id,
          title: item.title,
        });
      }
    }
    return selfChecks;
  };

  return (
    <RoadmapContext.Provider
      value={{
        roadmapItemMap,
        nextRoadmapItemMap,
        roadmapLoading,
        getCurrentTeachingModule,
        createModuleInDB,
        deleteModuleFromDB,
        createItemInDB,
        deleteItemFromDB,
        editModuleInDB,
        editItemInDB,
        bulkEditItemsInDB,
        getCommentPath,
        swapItemOrderInDB,
        addModuleImageLink,
        getAssignmentsInRoadmap,
        getLessonsInRoadmap,
        getTeacherLessonsInRoadmap,
        getSelfChecksInRoadmap,
      }}
    >
      {children}
    </RoadmapContext.Provider>
  );
};
